import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Snackbar, Alert } from "@mui/material";

export default function PdfGeneratorDialog({ open, onClose }) {
  const [formData, setFormData] = useState({
    year: "",
    department: "",
    municipality: "",
  });
  const [years, setYears] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const [userLocation, setUserLocation] = useState(null);
  const [lock, setLock] = useState({ department: false, municipality: false });

  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const user = JSON.parse(localStorage.getItem("user"));
          const resLocation = await fetch(`${process.env.REACT_APP_API_URL}/user-location?username=${user.username}`);
          const location = await resLocation.json();
          setUserLocation(location);

          const res = await fetch(`${process.env.REACT_APP_API_URL}/pdf/departamentos`);
          const data = await res.json();
          
          const departmentsWithAll = location.type === "central" 
            ? [{ departamentoId: "todos", departamentoName: "Todos los departamentos" }, ...data]
            : data;
          
          setDepartments(departmentsWithAll);

          if (location.type === "central") {
            setLock({ department: false, municipality: false });
            setFormData({ year: "", department: "", municipality: "" });
            setMunicipalities([]);
            setYears([]);
          } else if (location.type === "department") {
            setFormData({
              department: location.departamentoId,
              municipality: "0",
              year: "",
            });
            setLock({ department: true, municipality: false });
            setMunicipalities([]);
            setYears([]);
          } else if (location.type === "municipality") {
            setFormData({
              department: location.departamentoId,
              municipality: location.municipioId,
              year: "",
            });
            setLock({ department: true, municipality: true });
            setMunicipalities([]);
            setYears([]);
          }

        } catch (err) {
          console.error("Error al obtener user-location o departamentos:", err);
        }
      })();
    }
  }, [open]);


  useEffect(() => {
    if (formData.department && formData.department !== "todos") {
      (async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/pdf/municipios/${formData.department}`);
          const data = await res.json();

          let municipiosList = data;
          if (!userLocation || userLocation.type !== "municipality") {
            municipiosList = [{ municipioId: "0", municipioName: "Todos" }, ...data];
          }

          setMunicipalities(municipiosList);

          if (!userLocation || userLocation.type !== "municipality") {
            setFormData(prev => ({ ...prev, municipality: "0", year: "" }));
          }

          setYears([]);
        } catch (err) {
          console.error("Error al obtener municipios:", err);
        }
      })();
    } else if (formData.department === "todos") {
      setMunicipalities([]);
      setFormData(prev => ({ ...prev, municipality: "0", year: "" }));
      setYears([]);
    }
  }, [formData.department, userLocation]);


useEffect(() => {
  if (formData.department) {
    (async () => {
      try {
        let url;
        if (formData.department === "todos") {
          url = `${process.env.REACT_APP_API_URL}/pdf/anios-disponibles/todos/0`;
        } else if (formData.municipality !== "") {
          url = `${process.env.REACT_APP_API_URL}/pdf/anios-disponibles/${formData.department}/${formData.municipality}`;
        } else {
          setYears([]);
          setFormData(prev => ({ ...prev, year: "" }));
          return;
        }

        const res = await fetch(url);
        const data = await res.json();
        setYears(data);
        setFormData(prev => ({ ...prev, year: "" }));
      } catch (err) {
        console.error("Error al obtener años:", err);
        setYears([]);
        setFormData(prev => ({ ...prev, year: "" }));
      }
    })();
  }
}, [formData.department, formData.municipality]);


    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

  const cargarImagen = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = reject;
    });

  const fetchPreguntaMensual = async (preguntaNo) => {
    const { year, department, municipality } = formData;
    const meses = Array.from({ length: 12 }, (_, i) => i + 1);
    const resultados = [];

    for (const mes of meses) {
      const url = `${process.env.REACT_APP_API_URL}/pdf/resultados/${preguntaNo}?anio=${year}&departamentoId=${department}&municipioId=${municipality}&mes=${mes}`;
      try {
        const res = await fetch(url);
        const data = await res.json();
        resultados.push(data);
      } catch (err) {
        console.error(`Error cargando mes ${mes} de pregunta ${preguntaNo}:`, err);
        resultados.push(null);
      }
    }
    return resultados;
  };

const agregarTabla = (doc, startY, titulo, indicadores, year) => {
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const sanitizeText = (text) => {
    if (!text) return "";
    return text
      .normalize("NFKD")
      .replace(/[^\x00-\x7F]/g, "")
      .replace(/\r?\n|\r/g, " ")
      .trim();
  };

  const wrapComment = (text, maxLength = 80) => {
    const words = text.split(" ");
    let lines = [];
    let current = "";

    for (const word of words) {
      if ((current + word).length > maxLength) {
        lines.push(current.trim());
        current = word + " ";
      } else {
        current += word + " ";
      }
    }
    if (current.trim()) lines.push(current.trim());
    return lines.join("\n");
  };

  const body = [];
  indicadores.forEach((ind) => {
    const rowResult = [sanitizeText(ind.titulo)];
    const rowComment = [""];

    ind.meses.forEach((mesData, index) => {
      let resultado = "S/D";
      let comentario = "";

      if (mesData) {
        if (mesData.mensajeDenominador) {
          resultado = "No hay denominador ingresado";
        } else if (mesData.porcentajeAcumulado !== null) {
          resultado = `${mesData.porcentajeAcumulado}%\n(${mesData.numeradorAcumulado}/${mesData.denominador})`;
        } else if (mesData.resultados && Object.keys(mesData.resultados).length > 0) {
          const r = mesData.resultados;

          if (r.si !== undefined && r.no !== undefined) {
            resultado = `Sí: ${mesData.numeradorAcumulado || 0}  No: ${r.no || 0}`;
          } else if (r.promedio !== undefined && r.promedio !== null) {
            resultado = Number(r.promedio).toFixed(2);
          }
        }

        if (mesData.comentario && mesData.comentario.trim() !== "") {
          comentario = wrapComment(sanitizeText("*" + mesData.comentario), 70);
        }
      }

      rowResult.push(resultado);
      rowComment.push(comentario);
    });

    body.push(rowResult, rowComment);
  });

  autoTable(doc, {
    startY,
    head: [
      [
        {
          content: titulo,
          colSpan: 13,
          styles: { halign: "center", fillColor: [173, 216, 230], fontStyle: "bold" },
        },
      ],
      ["Indicador", ...meses],
    ],
    body,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4, valign: "middle" },
    columnStyles: { 0: { cellWidth: 150 } },
    headStyles: {
      fillColor: [128, 169, 207],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      halign: "center",
    },
    didParseCell: (data) => {
      const altoTotal = 100;

      if (data.section === "head" && data.row.index === 0) {
        data.cell.styles.fillColor = [200, 230, 255];
        data.cell.styles.textColor = [0, 0, 0];
      }

      if (data.section === "head" && data.row.index === 1) {
        data.cell.styles.fillColor = [1, 84, 159];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }

      if (data.section === "body" && data.column.index === 0 && data.row.raw[0] !== "") {
        data.cell.rowSpan = 2;
      }

      if (data.section === "body") {
        const isCommentRow = data.row.index % 2 === 1;
        if (isCommentRow) {
          data.cell.styles.fontSize = 8;
          data.cell.styles.fontStyle = "bolditalic";
          data.cell.styles.textColor = [80, 80, 80];
          data.cell.styles.fillColor = [230, 230, 230];
        } else {
          data.cell.styles.fontSize = 10;
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.halign = "center";
        }
      }
    },
  });
};

  const handleGenerate = async () => {
    const { year, department, municipality } = formData;
    if (!year || !department || municipality === "") {
      return alert("Selecciona todos los campos antes de generar el PDF.");
    }

    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const img = await cargarImagen("/images/sesan.png");

      const hojas = [
      {
        titulo: "Tabla 1. Marco de gobernanza territorial para el abordaje de la desnutrición aguda – Responsable: SESAN",
        preguntas: [
          { no: 1, titulo: "1. ¿En su municipio se tiene activada la Mesa técnica de atención a la Desnutrición aguda?" },
          { no: 2, titulo: "2. ¿En su municipio se tiene Plan Municipal de intervenciones de acuerdo al análisis de situación?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 3, titulo: "3. ¿En su municipio se hizo la identificación de determinantes de la Situación de la Denutrición Aguda?" },
          { no: 4, titulo: "4. ¿En el Plan municipal de intervenciones se tiene incluido el Club de Nutrición?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 5, titulo: "5. ¿En el Plan municipal de intervenciones se tiene incluida la Estrategia de Comunicación para el Conocimiento familiar y comunitario de DA? " },
          { no: 6, titulo: "6. ¿En el Plan municipal de intervenciones se tiene incluida la intensificación de acciones en el tema de Aguas y Saneamiento?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 7, titulo: "7. ¿En el Plan municipal de intervenciones se tiene incluida la realización de MODA y la Socialización de los resultados?" },
          { no: 8, titulo: "8. ¿En el Plan municipal de intervenciones se tiene incluido el Protocolo de Actuación Conjunta (Asistencia alimentaria, Producción de alimentos, Bono nutrición, Bono salud, Protocolo atención a DA de MSPAS, RSO?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 9, titulo: "9. ¿En el Plan municipal de intervenciones se tienen incluidas intervenciones y recursos del Gobierno municipal?" },
          { no: 10, titulo: "10. ¿Qué porcentaje de avance se tiene en el Plan Municipal de intervenciones?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 11, titulo: "11. ¿En su municipio se han priorizado comunidades para intensificar las acciones?" },
          { no: 12, titulo: "12. ¿Cuántas comunidades han sido priorizadas en su municipio?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 13, titulo: "13. ¿Cuántas comunidades priorizadas tienen redes de apoyo en su municipio?" },
          { no: 14, titulo: "14. ¿Cuál es el número total (acumulado) de niñas y niños con desnutrición aguda, del listado nominal, que han sido monitoreados?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 15, titulo: "15. ¿Cuál es el porcentaje de niñas y niños con Desnutrición Aguda Monitoreados a la fecha? " },
          { no: 16, titulo: "16. ¿En la reunión mensual de COMUSAN del municipio, se realizó presentación de resultados de MODA? " },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 17, titulo: "17. ¿En la COMUSAN de su municipio se elaboró informe registrando avances, desafíos y gestiones realizadas a nivel territorial y central?" },
          { no: 18, titulo: "18. ¿Ha inicios del mes que se está reportando recibieron el listado nominal de niñas y niños con Desnutrición aguda?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 19, titulo: "19. ¿Cuál es el número total (acumulado) de niñas y niños con desnutrición aguda, que cuentan CON CUI, que han sido incluidos en el listado nominal?" },
          { no: 20, titulo: "20. ¿Cuál es el número acumulado de niñas y niños con desnutrición aguda, SIN CUI que han sido incluidos en el listado nominal?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 21, titulo: "21. ¿Cuál es el número acumulado de niñas y niños que han sido localizados y trasladados a la delegación departamental para su reporte a Oficinas centrales?" },
          { no: 22, titulo: "22. ¿Cuál es el porcentaje de niñas y niños con Desnutrición Aguda, SIN CUI, que han sido localizados en el municipio?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 23, titulo: "23. ¿En el municipio se cuenta con el registro de familias que han sido beneficiadas con programas sociales o institucionales?" },
          { no: 24, titulo: "24. ¿Cuántas familias de niños con Desnutrición aguda, reciben el Bono Nutrición?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 25, titulo: "25. ¿Cuántas familias de niños con Desnutrición aguda, reciben Asistencia alimentaria por NDA?" },
          { no: 26, titulo: "26. ¿Cuántas familias de niños con Desnutrición aguda, reciben Atención del MSPAS?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 27, titulo: "27. ¿Cuántas familias de niños con Desnutrición aguda, reciben Asistencia técnica de MAGA para producción agropecuaria?" },
          { no: 28, titulo: "28. ¿Cuántas niñas y niños con Desnutrición aguda, del listado nominal, corresponden a las comunidades priorizadas?" },
        ],
      },
      {
        titulo: "Continuación de la Tabla 1. Aplicación del Protocolo de Actuación Conjunta para la atención inmediata en los municipios priorizados – Responsable:\nSESAN",
        preguntas: [
          { no: 29, titulo: "29. ¿Cuántas niñas y niños con Desnutrición aguda, que corresponden a las comunidades priorizadas, han sido monitoreados?" },
        ],
      },
    ];

  for (let i = 0; i < hojas.length; i++) {
        const hoja = hojas[i];
        const indicadores = [];

        for (const preg of hoja.preguntas) {
          const datos = await fetchPreguntaMensual(preg.no);
          indicadores.push({ titulo: preg.titulo, meses: datos });
        }

        if (i > 0) doc.addPage();

        const imgWidth = 200;
        const imgHeight = imgWidth * (img.height / img.width);
        doc.addImage(img, "PNG", 30, 20, imgWidth, imgHeight);

        doc.setFontSize(12);
        doc.text(`Avance del Protocolo... ${year}`, 40, 130);
        doc.text(hoja.titulo, 40, 150);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        
        let locationText = "";
        if (formData.department === "todos") {
          locationText = "Nacional";
        } else {
          const departamentoName = departments.find(d => d.departamentoId === formData.department)?.departamentoName || "";
          const municipioName = municipalities.find(m => m.municipioId === formData.municipality)?.municipioName || "";
          locationText = `${departamentoName} - ${municipioName}`;
        }
        
        doc.text(locationText, 800, 100, { align: "right" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        agregarTabla(doc, 170, hoja.titulo, indicadores, year);
      }

      doc.save(`reporte_indicadores_${year}.pdf`);

      onClose();
      setSnackbar({
        open: true,
        message: "PDF generado exitosamente",
        severity: "success",
      });

    } catch (error) {
      console.error("Error generando PDF:", error);
      setSnackbar({
        open: true,
        message: "Error al generar el PDF",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Generar Reporte PDF</DialogTitle>
        <DialogContent>
        <FormControl fullWidth margin="normal" disabled={lock.department || departments.length === 0}>
          <InputLabel>Departamento</InputLabel>
          <Select name="department" value={formData.department} onChange={handleChange}>
            {departments.map(d => (
              <MenuItem key={d.departamentoId} value={d.departamentoId}>{d.departamentoName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl 
          fullWidth 
          margin="normal" 
          disabled={!formData.department || formData.department === "todos" || lock.municipality}
        >
          <InputLabel>Municipio</InputLabel>
          <Select name="municipality" value={formData.municipality} onChange={handleChange}>
            {municipalities.map(m => (
              <MenuItem key={m.municipioId} value={m.municipioId}>{m.municipioName}</MenuItem>
            ))}
          </Select>
        </FormControl>

<FormControl
  fullWidth
  margin="normal"
  disabled={!formData.department || (formData.department !== "todos" && !formData.municipality)}
>
  <InputLabel>Año</InputLabel>
  <Select
    name="year"
    value={formData.year}
    onChange={handleChange}
  >
    {years.length > 0 ? (
      years.map((y) => (
        <MenuItem key={y} value={y}>
          {y}
        </MenuItem>
      ))
    ) : (
      <MenuItem value="" disabled>
        No hay datos disponibles
      </MenuItem>
    )}
  </Select>
</FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={
              !formData.department || 
              (formData.department !== "todos" && !formData.municipality) || 
              !formData.year || 
              years.length === 0
            }
          >
            Generar PDF
          </Button>

        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
